import React from 'react';
import { observer, inject } from 'mobx-react';
import { Theme, Typography, Button } from '@mui/material';
import { createStyles, WithStyles, withStyles } from '@mui/styles';
import { WithTranslation, withTranslation, Trans } from 'react-i18next';
import ErrorIcon from '@mui/icons-material/ErrorOutline';

import { WizardModel, WizardStep } from 'models/kiosk/WizardModel';
import { Billpay } from 'models/kiosk/Billpay';
import { Services } from 'helpers';
import { ReceiptGeneratingErrorModal } from 'containers/modals';
import StoreModel from 'models/Index';
import { ReceiptStep } from 'interfaces';

interface PrintReceiptStepProps
    extends WithStyles<typeof styles>,
        WithTranslation {}
interface PrintReceiptStepPropsInjected extends PrintReceiptStepProps {
    Store: StoreModel;
    wizard: WizardModel;
    billpay: Billpay;
}

interface PrintReceiptStepState {
    printerErrorMessage?: string;
}

const styles = (theme: Theme) =>
    createStyles({
        root: {
            margin: 'calc(10vh/100*5) 10% calc(10vh/100*5) 10%',
            display: 'flex',
            flexDirection: 'column',
        },
        body: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginTop:'5vh',
        },
        title: {
            color: theme.palette.primary.main,
            fontSize: '3.5rem',
            textAlign: 'center',
            maxWidth: '700px',
        },
        transactionTitle: {
            margin: '36px 0;',
            fontSize: '2.16rem',
        },
        transactionSuccessful: {
            textAlign: 'center',
            marginTop: '64px',
        },
        transactionError: {
            textAlign: 'center',
        },
        transactionErrorText: {
            marginTop: '60px',
        },
        buttons: {
            marginTop: '30px',
            fontSize: '2.16rem',

            '& button': {
                height: '104px',
                width: '504px',
                marginTop: '72px',
                display: 'block',
                fontSize: '1.25rem',
            },
        },
        printerError: {
            marginTop: '56px',
            display: 'flex',
            textAlign: 'center',
            alignItems: 'center',
        },
        printerErrorIcon: {
            marginRight: '15px',
            fill: 'red',
            width: '3em',
            height: '3em',
        },
        printerErrorText: {
            color: 'red',
        },
    });

@inject(Services.STORE, Services.PAYMENT_WIZARD, Services.BILLPAY)
@observer
class PrintReceiptStep extends React.Component<
    PrintReceiptStepProps,
    PrintReceiptStepState
> {
    get injected() {
        return this.props as PrintReceiptStepPropsInjected;
    }

    constructor(props: PrintReceiptStepProps) {
        super(props);
        const { Store, t } = this.injected;

        this.state = {
            printerErrorMessage: Store.isProceedWithoutPrinter
                ? t('Printer is not available now, please use email receipt')
                : undefined,
        };
    }

    render() {
        const { printerErrorMessage } = this.state;
        const { classes, t } = this.props;
        const { Store, wizard, billpay } = this.injected;

        return (
            <div className={classes.root}>
                <div className={classes.body}>
                    <Typography variant='h2' className={classes.title}>
                        {t('Thank You For Your Payment!')}
                    </Typography>
                    <div className={classes.transactionSuccessful}>
                        <Typography
                            className={classes.transactionTitle}
                            variant='h5'
                        >
                            {!billpay.isSuccess ? (
                                <Trans
                                    i18nKey='TransactionDelayedSuccess'
                                    values={{
                                        amountInserted:
                                            billpay.amountInserted.toFixed(2),
                                    }}
                                >
                                    <strong>
                                        ${billpay.amountInserted.toFixed(2)}
                                    </strong>{' '}
                                    has been applied to your account.
                                    <br />
                                    <br />
                                    Please allow up to 48 hours for your payment
                                    to be reflected in your balance due.
                                    <br />
                                    Should you have any questions, please
                                    contact customer support.
                                    <br />
                                    <br />
                                    <strong>Would you like a receipt?</strong>
                                </Trans>
                            ) : (
                                <Trans
                                    i18nKey='TransactionSuccess'
                                    values={{
                                        amountInserted:
                                            billpay.amountInserted.toFixed(2),
                                    }}
                                >
                                    <strong>
                                        ${billpay.amountInserted.toFixed(2)}
                                    </strong>{' '}
                                    has been applied to your account.
                                    <br />
                                    <br />
                                    <strong>Would you like a receipt?</strong>
                                </Trans>
                            )}
                        </Typography>
                    </div>
                    {printerErrorMessage && (
                        <div className={classes.printerError}>
                            <ErrorIcon className={classes.printerErrorIcon} />
                            <Typography
                                className={classes.printerErrorText}
                                variant='h5'
                            >
                                {printerErrorMessage}
                            </Typography>
                        </div>
                    )}

                    <div className={classes.buttons}>
                        <Button
                            disabled={!!printerErrorMessage}
                            variant='primary'
                            onClick={this.handlePrintReceiptClick.bind(
                                this,
                                wizard
                            )}
                        >
                            <Typography variant='button'>
                                {t('Print Receipt')}
                            </Typography>
                        </Button>
                        <Button
                            disabled={!Store.peripheryStatus.isKioskOnline}
                            variant='primary'
                            onClick={this.handleEmailReceiptClick.bind(
                                this,
                                wizard
                            )}
                        >
                            <Typography
                                variant='button'
                                style={{ marginTop: '30px' }}
                            >
                                {t('E-mail Receipt')}
                            </Typography>
                        </Button>
                        <Button
                            disabled={
                                !!printerErrorMessage ||
                                !Store.peripheryStatus.isKioskOnline
                            }
                            variant='primary'
                            onClick={this.handlePrintEmailReceiptClick.bind(
                                this,
                                wizard
                            )}
                        >
                            <Typography
                                variant='button'
                                style={{ marginTop: '30px' }}
                            >
                                {t('Print & E-mail Receipt')}
                            </Typography>
                        </Button>
                        {billpay.isSuccess && (
                            <Button
                                variant='secondary'
                                onClick={(_) => {
                                    this.setReceiptStep(ReceiptStep.None);
                                    wizard.goToStep(WizardStep.Farewell);
                                }}
                            >
                                <Typography variant='button'>
                                    {t('No Receipt')}
                                </Typography>
                            </Button>
                        )}
                    </div>
                </div>

                {billpay.receipts.generateFailed && (
                    <ReceiptGeneratingErrorModal
                        modalProps={{
                            open: true,
                            onClose: () => {
                                wizard.goToStep(WizardStep.EmailAddress);
                            },
                        }}
                        buttonProps={{
                            onClick: () => {
                                wizard.goToStep(WizardStep.EmailAddress);
                            },
                        }}
                    />
                )}
            </div>
        );
    }

    setReceiptStep(step: ReceiptStep) {
        this.injected.billpay.receipts.step = step;
    }

    handlePrintReceiptClick(wizard: { goToStep: (arg0: WizardStep) => void }) {
        this.setReceiptStep(ReceiptStep.Print);
        this.printReceipt().then(() => {
            wizard.goToStep(WizardStep.Farewell);
        });
    }

    handleEmailReceiptClick(wizard: { goToStep: (arg0: WizardStep) => void }) {
        this.setReceiptStep(ReceiptStep.Email);
        wizard.goToStep(WizardStep.EmailAddress);
    }

    handlePrintEmailReceiptClick(wizard: {
        goToStep: (arg0: WizardStep) => void;
    }) {
        this.setReceiptStep(ReceiptStep.PrintAndEmail);
        this.printReceipt().finally(() => {
            this.setReceiptStep(ReceiptStep.Email);
            wizard.goToStep(WizardStep.EmailAddress);
        });
    }

    printReceipt() {
        const { billpay, t, Store } = this.injected;

        Store.loader.show();

        return billpay.receipts
            .printReceipt()
            .catch((error) => {
                this.setState({
                    printerErrorMessage: t(
                        'Printer is not available now, please use email receipt'
                    ),
                });

                throw error;
            })
            .finally(() => Store.loader.hide());
    }
}

export default withTranslation()(withStyles(styles)(PrintReceiptStep));
